import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePlatform, usePlatformClients, usePlatformConfigurations, useCreatePlatformClient, useUpdatePlatformClient, useDeletePlatformClient } from '../hooks/usePlatforms';
import CreateClientModal from '../components/platforms/CreateClientModal';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  TvIcon,
  DeviceTabletIcon,
  TagIcon,
  ChartBarIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';


const PlatformDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'clients' | 'configurations'>('overview');
  const [showAddClient, setShowAddClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  // Fetch platform data
  const { data: platform, isLoading: platformLoading, error: platformError } = usePlatform(id!);
  const { data: clientsData, isLoading: clientsLoading } = usePlatformClients(id!);
  const { data: configurationsData, isLoading: configurationsLoading } = usePlatformConfigurations(id!);
  
  // Client mutations
  const createClientMutation = useCreatePlatformClient();
  const updateClientMutation = useUpdatePlatformClient();
  const deleteClientMutation = useDeletePlatformClient();

  const clients = clientsData?.clients || [];
  const configurations = (configurationsData as any)?.configurations || configurationsData || [];

  if (platformLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (platformError || !platform) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Platform bulunamadı</h2>
        <p className="text-gray-500 dark:text-gray-400">Aradığınız platform mevcut değil veya silinmiş olabilir.</p>
        <Link to="/platforms" className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-500">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Platformlara Dön
        </Link>
      </div>
    );
  }

  const getClientTypeIcon = (clientType: string) => {
    switch (clientType) {
      case 'web_app':
        return <ComputerDesktopIcon className="h-5 w-5" />;
      case 'mobile_app':
        return <DevicePhoneMobileIcon className="h-5 w-5" />;
      case 'tv_app':
        return <TvIcon className="h-5 w-5" />;
      default:
        return <DeviceTabletIcon className="h-5 w-5" />;
    }
  };

  const getClientTypeName = (clientType: string) => {
    switch (clientType) {
      case 'web_app':
        return 'Web Uygulaması';
      case 'mobile_web':
        return 'Mobil Web';
      case 'mobile_app':
        return 'Mobil Uygulama';
      case 'tv_app':
        return 'TV Uygulaması';
      default:
        return 'Diğer';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'inactive':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('tr-TR').format(num);
  };

  const handleCreateClient = async (clientData: any) => {
    try {
      await createClientMutation.mutateAsync({
        platformId: id!,
        data: clientData
      });
      setShowAddClient(false);
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const handleUpdateClient = async (clientData: any) => {
    if (!selectedClient) return;
    
    try {
      await updateClientMutation.mutateAsync({
        platformId: id!,
        clientId: selectedClient.id,
        data: clientData
      });
      setSelectedClient(null);
    } catch (error) {
      console.error('Failed to update client:', error);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (!confirm('Bu client\'ı silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteClientMutation.mutateAsync({
        platformId: id!,
        clientId
      });
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Genel Bakış', icon: EyeIcon },
    { id: 'clients', name: 'İstemciler', icon: DevicePhoneMobileIcon },
    { id: 'configurations', name: 'Konfigürasyonlar', icon: Cog6ToothIcon }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/platforms"
            className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {platform.name}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Platform detayları ve istemci uygulamaları
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600">
            <ChartBarIcon className="h-4 w-4 mr-2" />
            Analitik
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600">
            <PencilIcon className="h-4 w-4 mr-2" />
            Düzenle
          </button>
        </div>
      </div>

      {/* Platform Status */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <DevicePhoneMobileIcon className="h-12 w-12 text-blue-500" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                {platform.name}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {platform.description}
              </p>
              <div className="mt-2 flex items-center space-x-4">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(platform.status)}`}>
                  {platform.status === 'active' ? 'Aktif' : platform.status === 'draft' ? 'Taslak' : 'Pasif'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {clients.length} İstemci
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Son güncelleme
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {new Date(platform.updatedAt).toLocaleDateString('tr-TR')}
            </div>
          </div>
        </div>

        {/* Tags */}
        {platform.tags && platform.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {platform.tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              >
                <TagIcon className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon
                className={`mr-2 h-5 w-5 ${
                  activeTab === tab.id
                    ? 'text-blue-500 dark:text-blue-400'
                    : 'text-gray-400 group-hover:text-gray-500'
                }`}
              />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Statistics */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                İstatistikler
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(clients.reduce((sum, client) => sum + ((client as any).impressions || 0), 0))}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Toplam Gösterim
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(clients.reduce((sum, client) => sum + ((client as any).clicks || 0), 0))}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Toplam Tıklama
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Platform Bilgileri
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Kategori
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {platform.category || 'Belirtilmemiş'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Öncelik
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {platform.priority === 'high' ? 'Yüksek' : platform.priority === 'medium' ? 'Orta' : platform.priority === 'low' ? 'Düşük' : 'Normal'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Zaman Dilimi
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {platform.timezone || 'Belirtilmemiş'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Oluşturulma
                </dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {new Date(platform.createdAt).toLocaleDateString('tr-TR')}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {activeTab === 'clients' && (
        <div className="space-y-6">
          {/* Add Client Button */}
          <div className="flex justify-end">
            <button
              onClick={() => setShowAddClient(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Yeni İstemci
            </button>
          </div>

          {/* Clients Grid */}
          {clientsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {clients.map((client) => (
              <div
                key={client.id}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 text-blue-500">
                        {getClientTypeIcon(client.clientType)}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {client.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {getClientTypeName(client.clientType)}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedClient(client)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                        title="Düzenle"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Client Details */}
                  <div className="mt-4 space-y-2">
                    {client.url && (
                      <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">URL: </span>
                        <span className="text-gray-900 dark:text-white">{client.url}</span>
                      </div>
                    )}
                    {client.bundleName && (
                      <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Bundle: </span>
                        <span className="text-gray-900 dark:text-white">{client.bundleName}</span>
                      </div>
                    )}
                    {client.operatingSystem && (
                      <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">OS: </span>
                        <span className="text-gray-900 dark:text-white">{client.operatingSystem}</span>
                      </div>
                    )}
                    {client.appVersion && (
                      <div className="text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Versiyon: </span>
                        <span className="text-gray-900 dark:text-white">{client.appVersion}</span>
                      </div>
                    )}
                  </div>

                  {/* Statistics */}
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatNumber((client as any).impressions || 0)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Gösterim
                        </div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatNumber((client as any).clicks || 0)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Tıklama
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status and Last Activity */}
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                      {client.status === 'active' ? 'Aktif' : 'Pasif'}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {(client as any).lastActivity ? new Date((client as any).lastActivity).toLocaleDateString('tr-TR') : 'Belirtilmemiş'}
                    </span>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'configurations' && (
        <div className="space-y-6">
          {/* Add Configuration Button */}
          <div className="flex justify-end">
            <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600">
              <PlusIcon className="h-4 w-4 mr-2" />
              Yeni Konfigürasyon
            </button>
          </div>

          {/* Configurations List */}
          {configurationsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {configurations.map((config: any) => (
                <li key={config.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                          {config.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {config.configType === 'features' ? 'Özellik Ayarları' :
                           config.configType === 'styling' ? 'Görünüm Ayarları' :
                           config.configType === 'analytics' ? 'Analitik Ayarları' : 'Hedefleme Ayarları'}
                          {config.isDefault && ' • Varsayılan'}
                          {config.clientId && ` • ${clients.find(c => c.id === config.clientId)?.name}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(config.updatedAt).toLocaleDateString('tr-TR')}
                      </span>
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
                        title="Düzenle"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-300 dark:hover:text-red-400"
                        title="Sil"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Add Client Modal */}
      <CreateClientModal
        isOpen={showAddClient}
        onClose={() => setShowAddClient(false)}
        onSubmit={handleCreateClient}
        platformId={id!}
        isLoading={createClientMutation.isPending}
      />

      {/* Edit Client Modal */}
      {selectedClient && (
        <CreateClientModal
          isOpen={true}
          onClose={() => setSelectedClient(null)}
          onSubmit={handleUpdateClient}
          platformId={id!}
          isLoading={updateClientMutation.isPending}
          initialData={selectedClient}
        />
      )}
    </div>
  );
};

export default PlatformDetail; 